/** Officer Command Centre (ADR-0117): profiles, My shift, record cards, decide in place, alerts. */
export default {
  en: {
    shift: {
      title: "My shift",
      panels: "Left panel",
      profile: "Profile",
      refresh: "Refresh",
      noLists: "Your profile has no work lists yet. An organisation admin can add them in Administration › Profiles.",
      loading: "Loading…",
      failed: "Could not load",
      failedLong: "This list could not be loaded. Try Refresh, or open the agent that keeps it.",
      empty: "Nothing waiting here.",
      count_one: "{{count}} item waiting",
      count_other: "{{count}} items waiting",
      more_one: "{{count}} more in the full list",
      more_other: "{{count}} more in the full list",
    },
    workspace: {
      title: "Record",
      purposeLine: "{{type}} {{key}}",
      back: "Back to the Command Centre",
      open: "Open workspace",
      find: "Open a record",
      findType: "Kind of record",
      findKey: "Number or ID",
      findGo: "Open",
      noProfile: "Your duty profile does not describe this kind of record, so there is nothing to show here.",
      noProfileHint: "An organisation admin can add it in Administration › Profiles.",
      pick: "Open a record from the Command Centre: a work list, a record card or a name in an answer.",
      loading: "Loading the record…",
      failed: "The record could not be loaded. The request may have been refused, or the system did not answer.",
      needsApproval: "Opening this record waits for an approval first. It is in the reviewer inbox.",
      retry: "Try again",
      sections: "Parts of the record",
      empty: "Nothing in this part of the record.",
      facts: "Main facts",
      status: "Status",
      summary: "Summary",
      people: "People in this record",
      openPerson: "Open {{name}}",
      documents: "Documents and exhibits",
      openDocument: "Open",
      noPreview: "Not a document (kept as a physical exhibit)",
      viewer: "Document",
      closeViewer: "Close document",
      docLoading: "Opening the document…",
      docFailed: "The document could not be opened.",
      recorded: "Access is recorded. Reason: {{reason}}",
      changeReason: "Give another reason",
    },
    purpose: {
      title: "Why are you opening this record?",
      body: "This kind of record is opened only for a work reason. Your reason is sent with every lookup of {{key}} and kept in the audit trail.",
      choose: "Reason",
      note: "More detail",
      notePlaceholder: "For example the report or court reference",
      noteNeeded: "Write a few words to explain the reason.",
      submit: "Open the record",
      cancel: "Go back",
      reasons: {
        investigation: "Active investigation",
        court: "Court or prosecution request",
        detention: "Arrest or detention",
        review: "Supervisor review",
        citizen_request: "Request from the person concerned",
        other: "Other reason",
      },
    },
    analysis: {
      title: "Analyses",
      intro: "Each button asks an agent about this record. Answers stay here while you work.",
      run: "Start",
      again: "Ask again",
      working: "Working…",
      progress_one: "Working… {{count}} lookup done",
      progress_other: "Working… {{count}} lookups done",
      failed: "This did not work. The request may have been refused, or the agent did not answer.",
      waiting: "This waits for an approval. It is in the reviewer inbox.",
      nothing: "The agent gave no answer.",
      at: "Answered at {{time}}",
      details: "What happened",
      ask: "Your question",
      askPlaceholder: "Ask anything about this record",
      send: "Ask",
      you: "You",
      agent: "Answer",
    },
    record: {
      title: "Record",
      close: "Close",
      actions: "What you can do",
      unknownType: "Your profile does not describe this kind of record, so there is nothing to look up.",
      loading: "Looking it up…",
      failed: "This lookup did not work. The request may have been refused, or the system did not answer.",
      needsApproval: "This lookup waits for an approval first. It is in the reviewer inbox.",
      nothing: "Nothing found.",
    },
    task: { title: "Decision" },
    decide: {
      loading: "Loading the decision…",
      cannotRead: "Waiting for {{who}}.",
      reason: "Reason (needed to reject)",
      reasonNeeded: "Write a reason to reject.",
      approve: "Approve",
      reject: "Reject",
      inInbox: "Open in the inbox",
      signNote: "Your decision is signed and recorded, exactly as in the inbox.",
      approved: "Approved. The request carries on.",
      rejected: "Rejected. The agent is told why.",
      why: {
        not_approval: "Waiting for {{who}}.",
        closed: "This has already been decided.",
        own_request: "You asked for this, so someone else must approve it: waiting for {{who}}.",
        excluded: "You cannot sign this level (you asked for it or signed an earlier level): waiting for {{who}}.",
        already_signed: "You have already approved this. It still needs another person: waiting for {{who}}.",
        not_for_you: "This is addressed to {{who}}.",
        not_your_role: "Waiting for {{who}}.",
      },
      error: {
        validation: "Check the decision: {{message}}",
        sign: "The decision could not be signed: {{message}}",
        decision: "The decision was signed but not delivered: {{message}}. Try again from the inbox.",
      },
    },
    alerts: { label: "Alerts", check: "Check now", dismiss: "Dismiss" },
    suggest: { label: "Next steps", actionOn: "{{action}} · {{key}}", open: "Open {{key}}" },
    busy: {
      stopping: "Stopping…",
      queued: "Your message will be sent when this request finishes: “{{text}}”",
      sendNow: "Send now anyway",
      dontSend: "Don't send",
      sendNowNote: "Sending now starts a new request; the one going on carries on by itself.",
      statusWho: "About this request",
      notThat: "That's not what I asked — send it",
      status: {
        approval: "The request is waiting for approval by {{who}}.",
        approvalLevel: "The request is waiting for approval by {{who}} (level {{level}} of {{of}}).",
        answer: "The request is waiting for an answer from {{who}}.",
        waitFor: "The request is waiting for {{what}} (until {{until}}).",
        working_one: "Still working: {{done}} step done so far.",
        working_other: "Still working: {{done}} steps done so far.",
        workingStep: "Still working, now on: {{step}} ({{done}} done so far).",
        reminded: "Nobody has decided yet; a reminder went out.",
        escalated_one: "Nobody decided in time, so it went to more people.",
        escalated_other: "Nobody decided in time, so it went to more people ({{count}} times).",
      },
    },
    issues: {
      id_invalid: "Every id must be lower-case letters, digits, - or _ and unique.",
      id_duplicate: "Two profiles have the same id.",
      label_required: "Every name needs English or Arabic words.",
      roles_required: "Every profile needs at least one role.",
      role_invalid: "A role name is not valid.",
      agent_invalid: "An agent name is not valid.",
      agent_required: "A lookup or action needs an agent (its own, or the profile's default agent).",
      source_invalid: "A work list's source must be the inbox, cases or a system call.",
      call_required: "A system call needs its tool, action and (for record types) the argument name.",
      record_required: "A work list that opens a record needs its record type and field.",
      field_invalid: "A field name is not valid.",
      action_unknown: "A record type offers an action that is not in the profile.",
      action_target: "Every action needs an agent or a system call.",
      section_source: "A workspace part must show the record (lookup), a related lookup that exists (related:0) or a field of the record (part:field).",
      section_kind: "A workspace part has a kind that is not known.",
      section_open: "A workspace part that opens records needs the record type and the field holding its key.",
    },
    admin: {
      intro: "Command Centre profiles give people on duty their work lists (My shift), the records they look up and the actions they may start. Each profile is for the people holding its roles. Every lookup and action runs as a request through an agent, with the usual rules, approvals and records.",
      example: "Start from an example:",
      examples: { police: "Police duty officers", bank: "Bank payment operations" },
      simple: "Simple form",
      json: "JSON (advanced)",
      jsonShape: "The JSON must be an object with a list called profiles.",
      jsonInvalid: "This JSON cannot be read: {{message}}",
      none: "No profiles yet. Add one or start from an example.",
      addProfile: "Add a profile",
      removeProfile: "Remove this profile",
      id: "Id",
      name: "Name",
      english: "English",
      arabic: "Arabic",
      roles: "For people with these roles",
      defaultAgent: "Default agent",
      workLists: "Work lists (My shift)",
      addWorkList: "Add a work list",
      source: "Source",
      sources: { inbox: "Reviewer inbox", cases: "Cases", tool: "A system call" },
      tool: "Tool",
      action: "Action",
      agent: "Agent that does it",
      recordTypes: "Record types",
      addRecordType: "Add a record type",
      type: "Type",
      key: "Key field",
      lookupTool: "Look up with tool",
      lookupAction: "and action",
      actions: "Actions",
      addAction: "Add an action",
      actionRoles: "Only for these roles (optional)",
      remove: "Remove",
      moreInJson: "Arguments with placeholders ({user.unit}, {item.order_no}, {record.key}), filters, related lookups and alerts are set in the JSON tab.",
      reason: "Why are you changing this? (recorded)",
      reasonHint: "For example: add the night shift's work lists",
      save: "Save profiles",
      saved: "Profiles saved. People see them the next time they open the Command Centre.",
    },
  },
  ar: {
    shift: {
      title: "مناوبتي",
      panels: "اللوحة الجانبية",
      profile: "الملف",
      refresh: "تحديث",
      noLists: "لا توجد قوائم عمل في ملفك بعد. يمكن لمسؤول الجهة إضافتها من الإدارة › الملفات.",
      loading: "جارٍ التحميل…",
      failed: "تعذّر التحميل",
      failedLong: "تعذّر تحميل هذه القائمة. جرّب التحديث، أو افتح الوكيل المسؤول عنها.",
      empty: "لا شيء بالانتظار هنا.",
      count_zero: "لا عناصر بالانتظار",
      count_one: "عنصر واحد بالانتظار",
      count_two: "عنصران بالانتظار",
      count_few: "{{count}} عناصر بالانتظار",
      count_many: "{{count}} عنصرًا بالانتظار",
      count_other: "{{count}} عنصر بالانتظار",
      more_zero: "لا مزيد",
      more_one: "عنصر آخر في القائمة الكاملة",
      more_two: "عنصران آخران في القائمة الكاملة",
      more_few: "{{count}} عناصر أخرى في القائمة الكاملة",
      more_many: "{{count}} عنصرًا آخر في القائمة الكاملة",
      more_other: "{{count}} عنصر آخر في القائمة الكاملة",
    },
    workspace: {
      title: "السجل",
      purposeLine: "{{type}} {{key}}",
      back: "العودة إلى مركز القيادة",
      open: "فتح مساحة العمل",
      find: "فتح سجل",
      findType: "نوع السجل",
      findKey: "الرقم أو الهوية",
      findGo: "فتح",
      noProfile: "لا يصف ملف مناوبتك هذا النوع من السجلات، فلا يوجد ما يُعرض هنا.",
      noProfileHint: "يمكن لمسؤول المؤسسة إضافته من الإدارة › الملفات.",
      pick: "افتح سجلًا من مركز القيادة: من قائمة عمل أو بطاقة سجل أو اسم في إجابة.",
      loading: "جارٍ تحميل السجل…",
      failed: "تعذّر تحميل السجل. ربما رُفض الطلب أو لم يستجب النظام.",
      needsApproval: "فتح هذا السجل ينتظر اعتمادًا أولًا. وهو في صندوق المراجعة.",
      retry: "حاول مرة أخرى",
      sections: "أجزاء السجل",
      empty: "لا شيء في هذا الجزء من السجل.",
      facts: "أهم البيانات",
      status: "الحالة",
      summary: "الملخص",
      people: "الأشخاص في هذا السجل",
      openPerson: "فتح {{name}}",
      documents: "المستندات والمضبوطات",
      openDocument: "فتح",
      noPreview: "ليس مستندًا (محفوظ كمضبوطات مادية)",
      viewer: "المستند",
      closeViewer: "إغلاق المستند",
      docLoading: "جارٍ فتح المستند…",
      docFailed: "تعذّر فتح المستند.",
      recorded: "الاطلاع مسجَّل. السبب: {{reason}}",
      changeReason: "ذكر سبب آخر",
    },
    purpose: {
      title: "لماذا تفتح هذا السجل؟",
      body: "لا يُفتح هذا النوع من السجلات إلا لسبب يتعلق بالعمل. يُرسَل سببك مع كل بحث عن {{key}} ويُحفظ في سجل التدقيق.",
      choose: "السبب",
      note: "تفاصيل إضافية",
      notePlaceholder: "مثل رقم البلاغ أو مرجع المحكمة",
      noteNeeded: "اكتب بضع كلمات لتوضيح السبب.",
      submit: "فتح السجل",
      cancel: "رجوع",
      reasons: {
        investigation: "تحقيق جارٍ",
        court: "طلب من المحكمة أو النيابة",
        detention: "قبض أو توقيف",
        review: "مراجعة المشرف",
        citizen_request: "طلب من الشخص المعني",
        other: "سبب آخر",
      },
    },
    analysis: {
      title: "التحليلات",
      intro: "كل زر يسأل وكيلًا عن هذا السجل. تبقى الإجابات هنا أثناء عملك.",
      run: "ابدأ",
      again: "اسأل مجددًا",
      working: "جارٍ العمل…",
      progress_zero: "جارٍ العمل…",
      progress_one: "جارٍ العمل… تم بحث واحد",
      progress_two: "جارٍ العمل… تم بحثان",
      progress_few: "جارٍ العمل… تمت {{count}} عمليات بحث",
      progress_many: "جارٍ العمل… تمت {{count}} عملية بحث",
      progress_other: "جارٍ العمل… تمت {{count}} عملية بحث",
      failed: "لم ينجح ذلك. ربما رُفض الطلب أو لم يُجب الوكيل.",
      waiting: "ينتظر هذا اعتمادًا. وهو في صندوق المراجعة.",
      nothing: "لم يقدّم الوكيل إجابة.",
      at: "أُجيب في {{time}}",
      details: "ماذا حدث",
      ask: "سؤالك",
      askPlaceholder: "اسأل أي شيء عن هذا السجل",
      send: "اسأل",
      you: "أنت",
      agent: "الإجابة",
    },
    record: {
      title: "السجل",
      close: "إغلاق",
      actions: "ما يمكنك فعله",
      unknownType: "لا يصف ملفك هذا النوع من السجلات، لذا لا يوجد ما يُبحث عنه.",
      loading: "جارٍ البحث…",
      failed: "لم ينجح هذا البحث. ربما رُفض الطلب أو لم يستجب النظام.",
      needsApproval: "ينتظر هذا البحث اعتمادًا أولًا. تجده في صندوق المراجعة.",
      nothing: "لم يُعثر على شيء.",
    },
    task: { title: "القرار" },
    decide: {
      loading: "جارٍ تحميل القرار…",
      cannotRead: "بانتظار {{who}}.",
      reason: "السبب (مطلوب للرفض)",
      reasonNeeded: "اكتب سببًا للرفض.",
      approve: "اعتماد",
      reject: "رفض",
      inInbox: "افتح في صندوق المراجعة",
      signNote: "يُوقَّع قرارك ويُسجَّل تمامًا كما في صندوق المراجعة.",
      approved: "تم الاعتماد. يتابع الطلب.",
      rejected: "تم الرفض. يُبلَّغ الوكيل بالسبب.",
      why: {
        not_approval: "بانتظار {{who}}.",
        closed: "تم البتّ في هذا بالفعل.",
        own_request: "أنت من طلب هذا، لذا يجب أن يعتمده شخص آخر: بانتظار {{who}}.",
        excluded: "لا يمكنك توقيع هذا المستوى (طلبته أو وقّعت مستوى سابقًا): بانتظار {{who}}.",
        already_signed: "اعتمدت هذا بالفعل. ما زال يحتاج إلى شخص آخر: بانتظار {{who}}.",
        not_for_you: "هذا موجّه إلى {{who}}.",
        not_your_role: "بانتظار {{who}}.",
      },
      error: {
        validation: "راجع القرار: {{message}}",
        sign: "تعذّر توقيع القرار: {{message}}",
        decision: "وُقّع القرار لكنه لم يُسلَّم: {{message}}. حاول مرة أخرى من صندوق المراجعة.",
      },
    },
    alerts: { label: "التنبيهات", check: "تحقّق الآن", dismiss: "تجاهل" },
    suggest: { label: "الخطوات التالية", actionOn: "{{action}} · {{key}}", open: "افتح {{key}}" },
    busy: {
      stopping: "جارٍ الإيقاف…",
      queued: "ستُرسل رسالتك عند انتهاء هذا الطلب: «{{text}}»",
      sendNow: "أرسلها الآن على أي حال",
      dontSend: "لا ترسلها",
      sendNowNote: "الإرسال الآن يبدأ طلبًا جديدًا؛ ويستمر الطلب الجاري وحده.",
      statusWho: "عن هذا الطلب",
      notThat: "ليس هذا ما سألت عنه — أرسلها",
      status: {
        approval: "الطلب بانتظار اعتماد {{who}}.",
        approvalLevel: "الطلب بانتظار اعتماد {{who}} (المستوى {{level}} من {{of}}).",
        answer: "الطلب بانتظار إجابة من {{who}}.",
        waitFor: "الطلب بانتظار {{what}} (حتى {{until}}).",
        working_zero: "ما زال يعمل: لم تكتمل أي خطوة بعد.",
        working_one: "ما زال يعمل: اكتملت خطوة واحدة حتى الآن.",
        working_two: "ما زال يعمل: اكتملت خطوتان حتى الآن.",
        working_few: "ما زال يعمل: اكتملت {{done}} خطوات حتى الآن.",
        working_many: "ما زال يعمل: اكتملت {{done}} خطوة حتى الآن.",
        working_other: "ما زال يعمل: اكتملت {{done}} خطوة حتى الآن.",
        workingStep: "ما زال يعمل، الآن على: {{step}} (اكتمل {{done}} حتى الآن).",
        reminded: "لم يتخذ أحد قرارًا بعد؛ أُرسل تذكير.",
        escalated_zero: "لم يتخذ أحد قرارًا في الوقت المحدد، فأُحيل إلى أشخاص آخرين.",
        escalated_one: "لم يتخذ أحد قرارًا في الوقت المحدد، فأُحيل إلى أشخاص آخرين.",
        escalated_two: "لم يتخذ أحد قرارًا في الوقت المحدد، فأُحيل إلى أشخاص آخرين (مرتين).",
        escalated_few: "لم يتخذ أحد قرارًا في الوقت المحدد، فأُحيل إلى أشخاص آخرين ({{count}} مرات).",
        escalated_many: "لم يتخذ أحد قرارًا في الوقت المحدد، فأُحيل إلى أشخاص آخرين ({{count}} مرة).",
        escalated_other: "لم يتخذ أحد قرارًا في الوقت المحدد، فأُحيل إلى أشخاص آخرين ({{count}} مرة).",
      },
    },
    issues: {
      id_invalid: "يجب أن يتكوّن كل معرّف من أحرف لاتينية صغيرة أو أرقام أو - أو _ وأن يكون فريدًا.",
      id_duplicate: "لملفين المعرّف نفسه.",
      label_required: "يحتاج كل اسم إلى كلمات بالعربية أو الإنجليزية.",
      roles_required: "يحتاج كل ملف إلى دور واحد على الأقل.",
      role_invalid: "اسم دور غير صالح.",
      agent_invalid: "اسم وكيل غير صالح.",
      agent_required: "يحتاج البحث أو الإجراء إلى وكيل (خاص به أو الوكيل الافتراضي للملف).",
      source_invalid: "يجب أن يكون مصدر قائمة العمل صندوق المراجعة أو القضايا أو استدعاء نظام.",
      call_required: "يحتاج استدعاء النظام إلى الأداة والإجراء و(لأنواع السجلات) اسم الوسيط.",
      record_required: "تحتاج قائمة العمل التي تفتح سجلًا إلى نوع السجل والحقل.",
      field_invalid: "اسم حقل غير صالح.",
      action_unknown: "يعرض نوع سجل إجراءً غير موجود في الملف.",
      action_target: "يحتاج كل إجراء إلى وكيل أو استدعاء نظام.",
      section_source: "يجب أن يعرض جزء مساحة العمل السجل (lookup) أو بحثًا مرتبطًا موجودًا (related:0) أو حقلًا من السجل (part:field).",
      section_kind: "لجزء في مساحة العمل نوع غير معروف.",
      section_open: "يحتاج جزء مساحة العمل الذي يفتح سجلات إلى نوع السجل والحقل الذي يحمل مفتاحه.",
    },
    admin: {
      intro: "تمنح ملفات مركز القيادة المناوبين قوائم عملهم (مناوبتي) والسجلات التي يبحثون عنها والإجراءات التي يمكنهم بدؤها. كل ملف لمن يحمل أدواره. يجري كل بحث وإجراء كطلب عبر وكيل، بالقواعد والاعتمادات والسجلات المعتادة.",
      example: "ابدأ من مثال:",
      examples: { police: "الضباط المناوبون في الشرطة", bank: "عمليات المدفوعات في بنك" },
      simple: "نموذج مبسّط",
      json: "JSON (متقدم)",
      jsonShape: "يجب أن يكون JSON كائنًا فيه قائمة باسم profiles.",
      jsonInvalid: "تعذّرت قراءة JSON هذا: {{message}}",
      none: "لا توجد ملفات بعد. أضف ملفًا أو ابدأ من مثال.",
      addProfile: "أضف ملفًا",
      removeProfile: "احذف هذا الملف",
      id: "المعرّف",
      name: "الاسم",
      english: "بالإنجليزية",
      arabic: "بالعربية",
      roles: "لمن يحملون هذه الأدوار",
      defaultAgent: "الوكيل الافتراضي",
      workLists: "قوائم العمل (مناوبتي)",
      addWorkList: "أضف قائمة عمل",
      source: "المصدر",
      sources: { inbox: "صندوق المراجعة", cases: "القضايا", tool: "استدعاء نظام" },
      tool: "الأداة",
      action: "الإجراء",
      agent: "الوكيل المنفّذ",
      recordTypes: "أنواع السجلات",
      addRecordType: "أضف نوع سجل",
      type: "النوع",
      key: "حقل المفتاح",
      lookupTool: "ابحث بالأداة",
      lookupAction: "والإجراء",
      actions: "الإجراءات",
      addAction: "أضف إجراءً",
      actionRoles: "لهذه الأدوار فقط (اختياري)",
      remove: "حذف",
      moreInJson: "تُضبط الوسائط ذات العناصر النائبة ({user.unit} و{item.order_no} و{record.key}) والمرشحات وعمليات البحث المرتبطة والتنبيهات من تبويب JSON.",
      reason: "لماذا تغيّر هذا؟ (يُسجَّل)",
      reasonHint: "مثلًا: إضافة قوائم عمل المناوبة الليلية",
      save: "احفظ الملفات",
      saved: "حُفظت الملفات. يراها الأشخاص في المرة التالية التي يفتحون فيها مركز القيادة.",
    },
  },
};
